import { Box } from '@chakra-ui/react';
import { AgGridReact } from 'ag-grid-react';

import type { ReportGridProps } from './types/report-grid.props';

import { useReportGridConfiguration } from './hooks/report-grid-configuration.hook';

/**
 * Renders a single AG Grid instance for a report view, configured via the
 * `useReportGridConfiguration` hook.
 *
 * This is the leaf-level grid component. For the smart router that selects the
 * correct renderer based on `view` (including split and seed count layouts),
 * see {@link ReportGrid}.
 *
 * @template TData - The type of data rows displayed in the grid.
 * @see {@link ReportGridProps}
 *
 * @example
 * ```tsx
 * <AgReportGrid mailingId={123} view="summary" />
 * ```
 */
function AgReportGrid<TData extends Record<string, unknown>>({
  hasStatusBar = true,
  mailingId,
  view,
}: ReportGridProps) {
  const {
    columns,
    csvExportParams,
    data,
    defaultColumnDefinition,
    defaultColumnGroupDefinition,
    defaultGridOptions,
    excelExportParams,
    isLoading,
    onGridReady,
    theme,
  } = useReportGridConfiguration<TData>({
    hasStatusBar,
    mailingId,
    view,
  });

  return (
    <Box
      h="full"
      w="full">
      <AgGridReact
        columnDefs={columns}
        defaultColDef={defaultColumnDefinition}
        defaultColGroupDef={defaultColumnGroupDefinition}
        defaultCsvExportParams={csvExportParams}
        defaultExcelExportParams={excelExportParams}
        gridOptions={defaultGridOptions}
        includeHiddenColumnsInQuickFilter={true}
        loading={isLoading}
        onGridReady={onGridReady}
        pagination={false}
        rowData={data}
        theme={theme}
      />
    </Box>
  );
}

export default AgReportGrid;
